import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDesign } from '@/contexts/DesignContext'
import { useIsDesktop } from '@/hooks/useMediaQuery'
import { cn } from '@/lib/cn'

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

const DISMISS_KEY = 'velox.pwa_install_dismissed'

/**
 * Mobile PWA install banner populated from `/design` PWA payload.
 */
export function PWAInstallPrompt() {
  const { t } = useTranslation('common')
  const { design } = useDesign()
  const isDesktop = useIsDesktop()
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(
    null,
  )
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    if (isDesktop) return
    if (window.matchMedia('(display-mode: standalone)').matches) return
    try {
      if (localStorage.getItem(DISMISS_KEY) === '1') return
    } catch {
      /* ignore */
    }

    const onBip = (e: Event) => {
      e.preventDefault()
      setDeferred(e as BeforeInstallPromptEvent)
      setVisible(true)
    }
    window.addEventListener('beforeinstallprompt', onBip)

    // iOS has no beforeinstallprompt — still show soft banner
    const isIos =
      /iphone|ipad|ipod/i.test(navigator.userAgent) &&
      !(window.navigator as Navigator & { standalone?: boolean }).standalone
    if (isIos) setVisible(true)

    return () => window.removeEventListener('beforeinstallprompt', onBip)
  }, [isDesktop])

  if (!visible || isDesktop) return null

  const dismiss = () => {
    setVisible(false)
    try {
      localStorage.setItem(DISMISS_KEY, '1')
    } catch {
      /* ignore */
    }
  }

  const install = async () => {
    if (!deferred) return
    await deferred.prompt()
    const choice = await deferred.userChoice
    if (choice.outcome === 'accepted') setVisible(false)
    setDeferred(null)
  }

  return (
    <div
      className={cn(
        'fixed inset-x-0 bottom-[calc(3.75rem+env(safe-area-inset-bottom))] z-floating mx-3',
        'rounded border border-terminal-border bg-terminal-surface/95 p-3 shadow-2xl backdrop-blur-md',
        'animate-[toast-in_220ms_ease-out] lg:hidden',
      )}
      role="dialog"
      aria-label={t('pwa.installTitle')}
    >
      <div className="flex items-start gap-3">
        <img
          src={design.pwa.icon_url}
          alt=""
          className="h-11 w-11 shrink-0 rounded-lg border border-terminal-border object-cover"
        />
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-terminal-fg">
            {design.pwa.title}
          </p>
          <p className="mt-0.5 line-clamp-2 text-xs text-terminal-muted">
            {design.pwa.description}
          </p>
        </div>
        <button
          type="button"
          onClick={dismiss}
          className="text-terminal-muted hover:text-terminal-fg"
          aria-label={t('actions.close')}
        >
          ✕
        </button>
      </div>
      <div className="mt-3 flex gap-2">
        {deferred ? (
          <button
            type="button"
            onClick={() => void install()}
            className="h-9 flex-1 rounded bg-accent text-sm font-semibold text-white"
          >
            {t('pwa.install')}
          </button>
        ) : (
          <p className="flex-1 text-[11px] leading-snug text-terminal-muted">
            {t('pwa.iosHint')}
          </p>
        )}
        <button
          type="button"
          onClick={dismiss}
          className="h-9 rounded border border-terminal-border px-3 text-sm text-terminal-muted"
        >
          {t('pwa.later')}
        </button>
      </div>
    </div>
  )
}
